import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ClipboardList, Boxes, Landmark, MapPin, ArrowLeft, FileText, Wallet, TicketPercent, Inbox, Truck, ShoppingBag, ShoppingCart, Megaphone, BadgePercent, Undo2, UserCog, BriefcaseBusiness, Receipt, PackagePlus, Users, TrendingUp, Star, FolderTree, ChevronsLeft, ChevronsRight, Menu, X, Palette, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SECTIONS = [
  {
    label: "Overview",
    items: [{ to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" }],
  },
  {
    label: "Sales",
    items: [
      { to: "/admin/orders", icon: ClipboardList, label: "Orders" },
      { to: "/admin/customers", icon: Users, label: "Customers" },
      { to: "/admin/reviews", icon: Star, label: "Reviews" },
      { to: "/admin/coupons", icon: TicketPercent, label: "Coupons" },
      { to: "/admin/abandoned-carts", icon: ShoppingCart, label: "Carts" },
      { to: "/admin/campaigns", icon: Megaphone, label: "Campaigns" },
      { to: "/admin/offers", icon: BadgePercent, label: "Offers" },
      { to: "/admin/support-leads", icon: Inbox, label: "Leads" },
    ],
  },
  {
    label: "Catalog",
    items: [
      { to: "/admin/products", icon: Package, label: "Products" },
      { to: "/admin/categories", icon: FolderTree, label: "Categories" },
      { to: "/admin/themes", icon: Palette, label: "Themes" },
      { to: "/admin/inventory", icon: Boxes, label: "Inventory" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/admin/fulfillment", icon: Truck, label: "Fulfillment" },
      { to: "/admin/suppliers", icon: Truck, label: "Suppliers" },
      { to: "/admin/purchases", icon: ShoppingBag, label: "Purchases" },
      { to: "/admin/procurement", icon: PackagePlus, label: "Procurement" },
      { to: "/admin/returns", icon: Undo2, label: "Returns" },
      { to: "/admin/delivery", icon: MapPin, label: "Delivery" },
    ],
  },
  {
    label: "Finance",
    items: [
      { to: "/admin/prime", icon: Crown, label: "PRIME" },
      { to: "/admin/expenses", icon: Receipt, label: "Expenses" },
      { to: "/admin/credit", icon: Landmark, label: "Credit" },
      { to: "/admin/invoices", icon: FileText, label: "Invoices" },
      { to: "/admin/payments", icon: Wallet, label: "Payments" },
    ],
  },
  {
    label: "Owner",
    ownerOnly: true,
    items: [
      { to: "/admin/team", icon: UserCog, label: "Team" },
      { to: "/admin/business", icon: BriefcaseBusiness, label: "Business" },
      { to: "/admin/analytics", icon: TrendingUp, label: "Analytics" },
    ],
  },
];

function SidebarContent({ collapsed, onNavigate }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const sections = SECTIONS.filter((s) => !s.ownerOnly || user?.role === "owner");

  return (
    <div className="flex h-full flex-col">
      <div className={`flex items-center gap-3 border-b border-white/[0.06] px-4 py-5 ${collapsed ? "justify-center px-2" : ""}`}>
        {collapsed ? (
          <span className="h-8 w-1 shrink-0 rounded-full bg-amber-500" />
        ) : (
          <>
            <img src="/paqvero-logo-light.png" alt="PAQVERO" width="92" height="21" className="h-[21px] w-auto" />
            <span className="rounded-md border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] text-amber-500">
              Admin
            </span>
          </>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4" data-testid="admin-tabs" data-lenis-prevent>
        {sections.map((section, si) => (
          <div key={section.label} className={si > 0 ? "mt-4" : ""}>
            {collapsed ? (
              si > 0 && <div className="mx-auto mb-2 h-px w-6 bg-white/10" />
            ) : (
              <p className="mb-1.5 px-2 text-[9px] font-bold uppercase tracking-[0.22em] text-neutral-600">{section.label}</p>
            )}
            <div className="space-y-0.5">
              {section.items.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.end}
                  title={collapsed ? tab.label : undefined}
                  onClick={onNavigate}
                  data-testid={`admin-tab-${tab.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-lg text-[13px] font-bold transition-all ${
                      collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2"
                    } ${
                      isActive
                        ? "bg-amber-500/[0.12] text-amber-400 shadow-[inset_0_0_0_1px_rgba(255,159,28,0.28)]"
                        : "text-neutral-400 hover:bg-white/[0.04] hover:text-white"
                    }`
                  }
                >
                  <tab.icon className={`h-4 w-4 shrink-0 transition-colors`} />
                  {!collapsed && <span className="truncate">{tab.label}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className={`border-t border-white/[0.06] p-3 ${collapsed ? "flex justify-center" : ""}`}>
        <button
          data-testid="admin-back-to-site"
          onClick={() => navigate("/")}
          title={collapsed ? "Back to Site" : undefined}
          className={`flex items-center gap-2 rounded-full border border-white/15 text-xs font-bold text-neutral-300 transition-colors hover:border-amber-500/50 hover:text-amber-400 ${
            collapsed ? "p-2.5" : "w-full justify-center px-5 py-2.5"
          }`}
        >
          <ArrowLeft className="h-3.5 w-3.5 shrink-0" />
          {!collapsed && "Back to Site"}
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("admin-sidebar-collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("admin-sidebar-collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen pt-[72px] pq-admin-dark bg-app" data-testid="admin-layout">
      <div className="sticky top-[72px] z-30 flex items-center gap-3 border-b border-white/[0.06] bg-[#FFFEFA] px-4 py-3 lg:hidden">
        <button
          data-testid="admin-sidebar-mobile-toggle"
          onClick={() => setMobileOpen(true)}
          className="rounded-lg border border-white/15 p-2 text-neutral-300 transition-colors hover:border-amber-500/50 hover:text-amber-400"
          aria-label="Open admin menu"
        >
          <Menu className="h-4 w-4" />
        </button>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500">PAQVERO ADMIN</p>
      </div>

      <div className="flex items-start">
        <aside
          data-testid="admin-sidebar"
          className={`sticky top-[72px] hidden h-[calc(100vh-72px)] shrink-0 border-r border-white/[0.06] bg-[#FFFEFA] transition-[width] duration-300 lg:block ${
            collapsed ? "w-[68px]" : "w-60"
          }`}
        >
          <SidebarContent collapsed={collapsed} />
          <button
            data-testid="admin-sidebar-toggle"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="absolute -right-3 top-16 z-10 rounded-full border border-white/15 bg-[#F1EDE3] p-1.5 text-neutral-400 transition-colors hover:border-amber-500/50 hover:text-amber-400"
          >
            {collapsed ? <ChevronsRight className="h-3.5 w-3.5" /> : <ChevronsLeft className="h-3.5 w-3.5" />}
          </button>
        </aside>

        {mobileOpen && (
          <div className="fixed inset-0 z-[60] lg:hidden" data-testid="admin-sidebar-mobile-overlay">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
            <aside className="absolute inset-y-0 left-0 w-64 border-r border-white/[0.06] bg-[#FFFEFA]">
              <button
                data-testid="admin-sidebar-mobile-close"
                onClick={() => setMobileOpen(false)}
                aria-label="Close admin menu"
                className="absolute -right-3 top-6 z-10 rounded-full border border-white/15 bg-[#F1EDE3] p-1.5 text-neutral-400 hover:border-amber-500/50 hover:text-amber-400"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <SidebarContent collapsed={false} onNavigate={() => setMobileOpen(false)} />
            </aside>
          </div>
        )}

        <main className="min-w-0 flex-1 px-4 pb-20 pt-6 sm:px-6 lg:px-10" data-testid="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
